/**
 * @fileoverview Page for creating new products with initial variant.
 * @module features/products/pages/CreateProductPage
 */

import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, ChevronDown, Plus, X } from 'lucide-react'
import { format } from 'date-fns'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Kbd } from '@/components/ui/kbd'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import { ScrollReveal, CostInput, parseCostToNumber, ImageUrlPopover, DatePicker } from '@/shared/components/ui'
import { createProduct, updateProduct } from '@/lib/productService'

/** Schema for adding explicit variants (used for type inference only - validation happens on product submit) */
const _variantFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  sku: z.string().optional(),
  originalCost: z.string().optional(),
  originalCurrency: z.string().optional(),
  costDate: z.string().optional(),
  imageUrl: z.string().optional(),
})

type VariantFormValues = z.infer<typeof _variantFormSchema>

/** Variant item in the list */
interface VariantItem {
  id: string // temporary ID for list key
  name: string
  sku: string | undefined
  originalCost: number | null
  originalCurrency: string | null
  costDate: string | null
  imageUrl: string | null
}

const createProductFormSchema = z.object({
  productName: z.string().min(1, 'Product name is required'),
  sku: z.string().optional(), // Auto-generated from product name if omitted
  description: z.string().optional(),
  category: z.string().optional(),
  brand: z.string().optional(),
  imageUrl: z.string().url().optional().or(z.literal('')),
  originalCost: z.string().optional(),
  originalCurrency: z.string().optional(),
})

type CreateProductFormValues = z.infer<typeof createProductFormSchema>

export function CreateProductPage(): ReactElement {
  const { t } = useTranslation('features-products')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [detailsExpanded, setDetailsExpanded] = useState(false)

  // Explicit variants state
  const [explicitVariants, setExplicitVariants] = useState<VariantItem[]>([])
  const [variantsExpanded, setVariantsExpanded] = useState(false)
  // null = creating new variant, string = editing existing variant by id
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const isEditingVariant = selectedVariantId !== null
  const selectedVariant = isEditingVariant ? explicitVariants.find(v => v.id === selectedVariantId) : null

  // Variant form for adding new variants (no validation - validated on product submit)
  const variantForm = useForm<VariantFormValues>({
    defaultValues: {
      name: '',
      sku: '',
      originalCost: '',
      originalCurrency: 'PLN',
      costDate: '',
      imageUrl: '',
    },
  })

  const form = useForm<CreateProductFormValues>({
    resolver: zodResolver(createProductFormSchema),
    defaultValues: {
      productName: '',
      sku: '',
      description: '',
      category: '',
      brand: '',
      imageUrl: '',
      originalCost: '',
      originalCurrency: '',
    },
  })

  // Save current variant form state (without validation - validation happens on product create)
  const saveCurrentVariantState = (): void => {
    const values = variantForm.getValues()
    const hasContent = values.name?.trim()

    if (isEditingVariant && selectedVariantId) {
      // Update existing variant in-place
      setExplicitVariants(prev => prev.map(v =>
        v.id === selectedVariantId
          ? {
              ...v,
              name: values.name,
              sku: values.sku?.trim() || undefined,
              originalCost: values.originalCost ? parseCostToNumber(values.originalCost) : null,
              originalCurrency: values.originalCurrency?.trim() || null,
              costDate: values.costDate?.trim() || null,
              imageUrl: values.imageUrl?.trim() || null,
            }
          : v
      ))
    } else if (hasContent) {
      // Create new variant only if there's content
      const newVariant: VariantItem = {
        id: crypto.randomUUID(),
        name: values.name,
        sku: values.sku?.trim() || undefined,
        originalCost: values.originalCost ? parseCostToNumber(values.originalCost) : null,
        originalCurrency: values.originalCurrency?.trim() || null,
        costDate: values.costDate?.trim() || null,
        imageUrl: values.imageUrl?.trim() || null,
      }
      setExplicitVariants(prev => [...prev, newVariant])
    }
  }

  // Select a variant for editing (auto-saves current state)
  const handleSelectVariant = (variant: VariantItem): void => {
    // Auto-save current variant before switching
    saveCurrentVariantState()

    setSelectedVariantId(variant.id)
    variantForm.reset({
      name: variant.name,
      sku: variant.sku ?? '',
      originalCost: variant.originalCost?.toString() ?? '',
      originalCurrency: variant.originalCurrency ?? 'PLN',
      costDate: variant.costDate ?? '',
      imageUrl: variant.imageUrl ?? '',
    })
  }

  // Switch to create new variant mode (auto-saves current state)
  const handleCreateNewMode = (): void => {
    // Auto-save current variant before switching
    saveCurrentVariantState()

    setSelectedVariantId(null)
    variantForm.reset({
      name: '',
      sku: '',
      originalCost: '',
      originalCurrency: 'PLN',
      costDate: '',
      imageUrl: '',
    })
  }

  // Remove a variant from the list
  const handleRemoveVariant = (id: string): void => {
    setExplicitVariants(prev => prev.filter(v => v.id !== id))
    // If we're editing the variant being removed, switch to create mode
    if (selectedVariantId === id) {
      handleCreateNewMode()
    }
  }

  const createMutation = useMutation({
    mutationFn: async ({ values, variants }: { values: CreateProductFormValues; variants: VariantItem[] }) => {
      const hasVariants = variants.length > 0

      // Create product with variants in a single API call
      const product = await createProduct({
        name: values.productName.trim(),
        description: values.description?.trim() || undefined,
        brand: values.brand?.trim() || undefined,
        imageUrl: values.imageUrl?.trim() || undefined,
        // Discrete variant fields (only used if no explicit variants)
        sku: hasVariants ? undefined : values.sku?.trim() || undefined,
        originalCost: hasVariants ? undefined : (values.originalCost ? parseCostToNumber(values.originalCost) : undefined),
        originalCurrency: hasVariants ? undefined : (values.originalCurrency?.trim() || undefined),
        // Explicit variants (if provided, no discrete variant is created)
        variants: hasVariants ? variants.map(v => ({
          name: v.name,
          sku: v.sku || undefined,
          originalCost: v.originalCost,
          originalCurrency: v.originalCurrency,
          costDate: v.costDate,
          imageUrl: v.imageUrl,
        })) : undefined,
      })

      // Update category if provided (category uses name lookup, not UUID)
      const trimmedCategory = values.category?.trim()
      if (trimmedCategory) {
        await updateProduct(product.id, { category: trimmedCategory })
      }

      return product
    },
    onSuccess: async (product) => {
      toast.success(t('create.success'))
      await queryClient.invalidateQueries({ queryKey: ['products'] })
      await queryClient.invalidateQueries({ queryKey: ['variant', 'search'] })
      navigate(`/dashboard/management/products/${product.id}`)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t('create.errors.failed'))
    },
  })

  const handleSubmit = async (values: CreateProductFormValues): Promise<void> => {
    // Build final variants list including any pending form data
    const pendingFormValues = variantForm.getValues()
    const hasPendingVariant = pendingFormValues.name?.trim()

    let finalVariants: VariantItem[]
    if (isEditingVariant && selectedVariantId) {
      // Update the selected variant with current form values
      finalVariants = explicitVariants.map(v =>
        v.id === selectedVariantId
          ? {
              ...v,
              name: pendingFormValues.name,
              sku: pendingFormValues.sku?.trim() || undefined,
              originalCost: pendingFormValues.originalCost ? parseCostToNumber(pendingFormValues.originalCost) : null,
              originalCurrency: pendingFormValues.originalCurrency?.trim() || null,
              costDate: pendingFormValues.costDate?.trim() || null,
              imageUrl: pendingFormValues.imageUrl?.trim() || null,
            }
          : v
      )
    } else if (hasPendingVariant) {
      // Add pending new variant to the list
      finalVariants = [...explicitVariants, {
        id: crypto.randomUUID(),
        name: pendingFormValues.name,
        sku: pendingFormValues.sku?.trim() || undefined,
        originalCost: pendingFormValues.originalCost ? parseCostToNumber(pendingFormValues.originalCost) : null,
        originalCurrency: pendingFormValues.originalCurrency?.trim() || null,
        costDate: pendingFormValues.costDate?.trim() || null,
        imageUrl: pendingFormValues.imageUrl?.trim() || null,
      }]
    } else {
      finalVariants = explicitVariants
    }

    // Validate variants - all must have a name
    const invalidVariants = finalVariants.filter(v => !v.name?.trim())
    if (invalidVariants.length > 0) {
      toast.error('All variants must have a name')
      return
    }

    setIsSubmitting(true)
    try {
      await createMutation.mutateAsync({ values, variants: finalVariants })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Esc to go back (only when form is not dirty)
  const isDirty = form.formState.isDirty
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isDirty) {
        event.preventDefault()
        navigate('/dashboard/management/products')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDirty, navigate])

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-screen-lg mx-auto px-4 lg:px-8 pt-24 pb-8 space-y-8">
        <ScrollReveal blur>
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/management/products')}
              className="gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {t('createPage.title')}
              </h1>
              <p className="text-muted-foreground">
                {t('createPage.subtitle')}
              </p>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
              <section className="flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Left Column: Image */}
                  <div className="md:col-span-1">
                    <ImageUrlPopover
                      trigger={
                        <button
                          type="button"
                          className="aspect-square w-full relative rounded-md overflow-hidden border border-border bg-muted/30 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          {form.watch('imageUrl')?.trim() ? (
                            <img
                              src={form.watch('imageUrl')}
                              alt={form.watch('productName') || 'Product'}
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <span className="text-muted-foreground text-sm">{t('create.clickToSetImage')}</span>
                          )}
                        </button>
                      }
                      currentImageUrl={form.watch('imageUrl') ?? null}
                      title={t('admin.imagePopover.title')}
                      description={t('admin.imagePopover.description')}
                      inputAriaLabel={t('admin.imagePopover.inputLabel')}
                      onSave={async (url) => {
                        form.setValue('imageUrl', url ?? '', { shouldDirty: true })
                      }}
                      successMessage={t('admin.toasts.imageUpdateSuccess')}
                      errorMessage={t('admin.toasts.imageUpdateError')}
                    />
                  </div>

                  {/* Right Column: Form Fields */}
                  <div className="md:col-span-2 space-y-6">
                    <FormField
                      control={form.control}
                      name="productName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('create.fields.productName')}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t('create.placeholders.productName')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* SKU and Cost fields - hidden when adding explicit variants */}
                    {!variantsExpanded && explicitVariants.length === 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField
                          control={form.control}
                          name="sku"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('create.fields.skuSlug')}</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t('create.placeholders.skuSlug')}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <CostInput
                          label={t('create.fields.originalCost')}
                          costValue={form.watch('originalCost') ?? ''}
                          onCostChange={(val) => form.setValue('originalCost', val, { shouldDirty: true })}
                          currencyValue={form.watch('originalCurrency') ?? undefined}
                          onCurrencyChange={(val) => form.setValue('originalCurrency', val, { shouldDirty: true })}
                        />
                      </div>
                    )}

                    {/* Progressive disclosure for optional fields */}
                    {!detailsExpanded ? (
                      <div className="flex flex-wrap gap-2">
                        {[
                          { key: 'description', label: t('create.fields.description'), value: form.watch('description') },
                          { key: 'category', label: t('create.fields.category'), value: form.watch('category') },
                          { key: 'brand', label: t('create.fields.brand'), value: form.watch('brand') },
                        ].map(pill => (
                          <Badge
                            key={pill.key}
                            variant="outline"
                            className="cursor-pointer rounded-full px-2.5 py-1 text-[11px] bg-muted/30 text-muted-foreground border-border/20 hover:bg-muted/50"
                            onClick={() => setDetailsExpanded(true)}
                          >
                            <span className="truncate max-w-[7rem]">
                              {pill.value?.trim() ? pill.value : pill.label}
                            </span>
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <button
                          type="button"
                          className="flex w-full items-center justify-between rounded-md px-1 py-1 text-xs font-medium text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => setDetailsExpanded(false)}
                        >
                          <span>{t('create.labels.additionalDetails')}</span>
                          <ChevronDown className="h-4 w-4 rotate-180" />
                        </button>

                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('create.fields.description')}</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder={t('create.placeholders.description')}
                                  className="min-h-[100px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="category"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('create.fields.category')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('create.placeholders.category')}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="brand"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('create.fields.brand')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('create.placeholders.brand')}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Variants Section */}
              <section className="flex flex-col rounded-3xl border border-border/15 bg-card overflow-hidden">
                {!variantsExpanded && explicitVariants.length === 0 ? (
                  // Collapsed state: simple row with "Add variant" button
                  <button
                    type="button"
                    onClick={() => {
                      // Copy product-level SKU/Cost to first variant form before expanding
                      const productSku = form.getValues('sku')
                      const productCost = form.getValues('originalCost')
                      const productCurrency = form.getValues('originalCurrency')
                      variantForm.setValue('sku', productSku ?? '')
                      variantForm.setValue('originalCost', productCost ?? '')
                      variantForm.setValue('originalCurrency', productCurrency || 'PLN')
                      setVariantsExpanded(true)
                    }}
                    className="flex items-center gap-3 px-6 py-4 text-left hover:bg-muted/30 transition-colors group"
                  >
                    <div className="h-8 w-8 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Plus className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">{t('variants.addVariant')}</div>
                      <div className="text-xs text-muted-foreground">{t('variants.addVariantDescription')}</div>
                    </div>
                  </button>
                ) : (
                  // Expanded state: two-column layout matching ProductVariantsManager
                  <div className="flex h-full">
                    {/* Left Sidebar: Variant List */}
                    <div className="border-border/15 flex flex-col bg-muted/10 w-1/3 border-r">
                      <div className="p-3 sm:p-4 border-b border-border/15 flex items-center justify-between shrink-0">
                        <h3 className="text-sm font-medium text-muted-foreground">{t('variants.title')}</h3>
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                            {explicitVariants.length}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              const values = variantForm.getValues()

                              if (isEditingVariant && selectedVariantId) {
                                // Update existing variant in-place
                                setExplicitVariants(prev => prev.map(v =>
                                  v.id === selectedVariantId
                                    ? {
                                        ...v,
                                        name: values.name,
                                        sku: values.sku?.trim() || undefined,
                                        originalCost: values.originalCost ? parseCostToNumber(values.originalCost) : null,
                                        originalCurrency: values.originalCurrency?.trim() || null,
                                        costDate: values.costDate?.trim() || null,
                                        imageUrl: values.imageUrl?.trim() || null,
                                      }
                                    : v
                                ))
                              } else {
                                // Always create new variant (even if empty - validation at submit)
                                const newVariant: VariantItem = {
                                  id: crypto.randomUUID(),
                                  name: values.name,
                                  sku: values.sku?.trim() || undefined,
                                  originalCost: values.originalCost ? parseCostToNumber(values.originalCost) : null,
                                  originalCurrency: values.originalCurrency?.trim() || null,
                                  costDate: values.costDate?.trim() || null,
                                  imageUrl: values.imageUrl?.trim() || null,
                                }
                                setExplicitVariants(prev => [...prev, newVariant])
                              }

                              // Switch to fresh create mode
                              setSelectedVariantId(null)
                              variantForm.reset({
                                name: '',
                                sku: '',
                                originalCost: '',
                                originalCurrency: 'PLN',
                                costDate: '',
                                imageUrl: '',
                              })
                            }}
                            title={t('variants.addNew')}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="p-2 flex-1 overflow-y-auto space-y-1">
                        {/* "New Variant" indicator - clickable to switch to create mode */}
                        <button
                          type="button"
                          onClick={handleCreateNewMode}
                          className={`text-left rounded-lg text-sm font-medium flex items-center gap-3 px-3 py-3 w-full transition-colors ${
                            !isEditingVariant
                              ? 'bg-secondary text-secondary-foreground'
                              : 'hover:bg-muted/50 text-muted-foreground'
                          }`}
                        >
                          <div className={`h-8 w-8 rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center ${
                            !isEditingVariant
                              ? 'bg-primary/20 border border-primary/30'
                              : 'bg-muted border border-border/10'
                          }`}>
                            <Plus className={`h-4 w-4 ${!isEditingVariant ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs truncate">{t('variants.newVariant')}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {!isEditingVariant ? t('variants.creating') : ''}
                            </div>
                          </div>
                        </button>
                        {/* Existing variants in the list */}
                        {explicitVariants.map((variant) => {
                          const isSelected = selectedVariantId === variant.id
                          return (
                            <button
                              key={variant.id}
                              type="button"
                              onClick={() => handleSelectVariant(variant)}
                              className={`text-left rounded-lg text-sm transition-colors flex items-center px-3 py-3 w-full gap-3 ${
                                isSelected
                                  ? 'bg-secondary text-secondary-foreground'
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                            >
                              <div className="h-8 w-8 rounded-md bg-muted border border-border/10 overflow-hidden flex-shrink-0">
                                {variant.imageUrl ? (
                                  <img src={variant.imageUrl} alt={variant.name} className="h-full w-full object-cover" />
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center bg-muted text-xs text-muted-foreground">
                                    {t('variants.imgPlaceholder')}
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs truncate">{variant.name || t('variants.unnamedVariant')}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {variant.sku || '—'}
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-destructive flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRemoveVariant(variant.id)
                                }}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {/* Right Content: Add Variant Form */}
                    <div className="flex-1 flex flex-col overflow-y-auto">
                      <div className="max-w-2xl mx-auto w-full p-8">
                        <div className="flex items-center mb-8 gap-4">
                          <ImageUrlPopover
                            trigger={
                              <div className={`h-20 w-20 rounded-xl border ${isEditingVariant ? 'border-border' : 'border-dashed border-border'} bg-muted/30 flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden flex-shrink-0`}>
                                {variantForm.watch('imageUrl') ? (
                                  <img
                                    src={variantForm.watch('imageUrl') ?? ''}
                                    alt={isEditingVariant ? selectedVariant?.name : 'New variant'}
                                    className="object-cover w-full h-full"
                                  />
                                ) : (
                                  <span className="text-muted-foreground text-xs text-center px-1">{t('variants.placeholders.addImage')}</span>
                                )}
                              </div>
                            }
                            currentImageUrl={variantForm.watch('imageUrl') ?? null}
                            title={t('variants.imagePopover.title')}
                            description={t('variants.imagePopover.description')}
                            inputAriaLabel={t('variants.imagePopover.inputLabel')}
                            onSave={async (url) => {
                              variantForm.setValue('imageUrl', url ?? '')
                            }}
                            successMessage={t('variants.toasts.imageUrlSet')}
                            errorMessage={t('variants.toasts.imageUrlError')}
                          />

                          <div className="flex-1 min-w-0">
                            <h2 className="text-2xl font-bold tracking-tight">
                              {isEditingVariant ? selectedVariant?.name || t('variants.unnamedVariant') : t('variants.createTitle')}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-0.5 truncate">
                              {isEditingVariant
                                ? (selectedVariant?.sku || '—')
                                : t('createPage.variantsHint')
                              }
                            </p>
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label>{t('variants.fields.name')}</Label>
                              <Input
                                placeholder={t('variants.placeholders.name')}
                                value={variantForm.watch('name')}
                                onChange={(e) => variantForm.setValue('name', e.target.value)}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>{t('variants.fields.sku')}</Label>
                              <Input
                                placeholder={t('variants.placeholders.sku')}
                                value={variantForm.watch('sku')}
                                onChange={(e) => variantForm.setValue('sku', e.target.value)}
                              />
                            </div>

                            <CostInput
                              costValue={variantForm.watch('originalCost') ?? ''}
                              onCostChange={(val) => variantForm.setValue('originalCost', val)}
                              currencyValue={variantForm.watch('originalCurrency')}
                              onCurrencyChange={(val) => variantForm.setValue('originalCurrency', val)}
                            />

                            <div className="space-y-2">
                              <Label>{t('variants.fields.costDate')}</Label>
                              <DatePicker
                                value={variantForm.watch('costDate') ? new Date(variantForm.watch('costDate')!) : null}
                                onChange={(date) => variantForm.setValue('costDate', date ? format(date, 'yyyy-MM-dd') : '')}
                                placeholder={t('variants.placeholders.selectDate')}
                              />
                            </div>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {/* Submit buttons */}
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard/management/products')}
                  disabled={isSubmitting}
                >
                  {t('create.actions.cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('create.actions.saving')}
                    </>
                  ) : (
                    t('create.actions.submit')
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </ScrollReveal>
      </div>
    </div>
  )
}

export default CreateProductPage
